import React, {FunctionComponent} from "react";
import {productSearchToggleFiltersAction} from "../../../action/ProductSearchToggleFiltersAction";
import {
    AccordionDetails,
    AccordionSummary,
    Box,
    Collapse,
    FormControl, Grid,
    InputAdornment,
    InputLabel, MenuItem,
    OutlinedInput, Select
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {FilterTitle} from "./FilterTitle";
import {FilterChip} from "./FilterChip";
import {updateLastSearchFilterAction} from "../../../action/UpdateLastSearchFilterAction";
import {StringUtils} from "../../../book/StringUtils";
import SearchIcon from "@mui/icons-material/Search";
import {ProductPropertyFilter} from "./ProductPropertyFilter";
import {FilterAccordion} from "./FilterAccordion";
import {useAppDispatch, useAppSelector} from "../../../store/AppStore";
import {useTranslation} from "react-i18next";
import {MetadataCategoryCode} from "../../../book/MetadataCategoryCode";
import {ProductPropertyData} from "../../../api/GovernanceToolApi";

const filterableCategories = Object.values(MetadataCategoryCode);

export const Filters: FunctionComponent<ProductSearchFiltersProps> = props => {
    const {t} = useTranslation();
    const {isFiltersExpanded, filterSearch, filterReleaseType, filterProductProperties} = useAppSelector(state => (state.productSearchState));
    const dispatch = useAppDispatch();

    const selectedFilterProductProperty = (category: string): ProductPropertyData | null =>
        filterProductProperties.hasOwnProperty(category) ? filterProductProperties[category] : null;

    return <FilterAccordion expanded={isFiltersExpanded} onChange={() => dispatch(productSearchToggleFiltersAction())}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
            <FilterTitle>{t("Filters")}:</FilterTitle>
            <Collapse in={!isFiltersExpanded}>
                <Box sx={{marginLeft: '2em',}}>
                    {filterSearch &&
                        <FilterChip label={`${t("Search")}: ${filterSearch}`}
                                    onDelete={() => {
                                        dispatch(updateLastSearchFilterAction('', filterProductProperties, filterReleaseType));
                                        props.callSearchProducts();
                                    }}
                                    variant='outlined'/>}

                    {Object.keys(filterProductProperties).map(category => {
                        const filterValue = selectedFilterProductProperty(category)?.value;
                        return <FilterChip
                            key={`${category ?? ''}-${filterValue ?? ''}`}
                            label={`${StringUtils.capitalizeFirstLetter(category ?? '')}: ${filterValue}`}
                            onDelete={() => {
                                const newFilterValues = {...filterProductProperties};
                                delete newFilterValues[category];

                                dispatch(updateLastSearchFilterAction(filterSearch, newFilterValues, filterReleaseType));
                                props.callSearchProducts();
                            }}
                            variant='outlined'/>
                    })}

                    {filterReleaseType !== undefined &&
                        <FilterChip
                            label={`${t("Release")}: ${filterReleaseType === 0 ? t("Stable") : t("Current")}`}
                            onDelete={() => {
                                dispatch(updateLastSearchFilterAction(filterSearch, filterProductProperties, undefined));
                                props.callSearchProducts();
                            }}
                            variant='outlined'/>}
                </Box>
            </Collapse>
        </AccordionSummary>
        <AccordionDetails>
            <Box sx={{flex: 1}}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel htmlFor="search-products">{t("Search by product name…")}</InputLabel>
                    <OutlinedInput
                        id="search-products"
                        value={filterSearch}
                        fullWidth
                        label={t("Search by product name…")}
                        sx={{marginBottom: '20px'}}
                        onChange={e => dispatch(updateLastSearchFilterAction(e.target.value, filterProductProperties, filterReleaseType))}
                        endAdornment={<InputAdornment position="end"><SearchIcon/></InputAdornment>}/>
                </FormControl>

                <Grid container spacing={2}>
                    {filterableCategories.map(category => {
                        return <Grid item xs={12} sm={6} key={`filter-${category}`}>
                            <ProductPropertyFilter category={category}
                                                   onChange={(newValue) => {
                                                       const newSelectedFilterValues = {...filterProductProperties};

                                                       if (newValue) {
                                                           newSelectedFilterValues[category] = newValue;
                                                       } else {
                                                           delete newSelectedFilterValues[category];
                                                       }

                                                       dispatch(updateLastSearchFilterAction(filterSearch, newSelectedFilterValues, filterReleaseType));
                                                   }}
                                                   value={selectedFilterProductProperty(category)}/>
                        </Grid>
                    })}
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined"
                                     fullWidth>
                            <InputLabel id="release-select-outlined-label">Release</InputLabel>
                            <Select
                                labelId="release-select-outlined-label"
                                id="release-select-outlined"
                                value={filterReleaseType ?? -1}
                                onChange={(event) => {
                                    const value = event.target.value;

                                    dispatch(updateLastSearchFilterAction(filterSearch, filterProductProperties, value === 0 || value === 1 ? value : undefined));
                                }}
                                label="Release">
                                <MenuItem value={-1}> <em>{t("All")}</em> </MenuItem>
                                <MenuItem value={0}>Stable</MenuItem>
                                <MenuItem value={1}>Current</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
        </AccordionDetails>
    </FilterAccordion>;
}

export interface ProductSearchFiltersProps{
    callSearchProducts: ()=> void;
}